import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";

import {
  acceptPublicSkillProfileClaim,
  applyPublicSkillGitHubUpdate,
  checkPublicSkillGitHubUpdate,
  loadMyPublicSkills,
  loadMyPublicSkillProfile,
  loadPublicSkill,
  loadPublicSkillCatalog,
  loadPublicSkillComparison,
  loadPublicSkillInstall,
  loadPublicSkillPreview,
  loadPublicSkillProfile,
  loadPublicSkillVersion,
  loadPublicSkillVersions,
  inspectPublicSkill,
  publishPublicSkill,
  publishPublicSkillVersion,
  ratePublicSkill,
  saveMyPublicSkillProfile,
  updateMyPublicSkill,
  type OwnedPublicSkill,
  type PublicSkill,
  type PublicSkillCatalog,
  type PublicSkillProfile,
  type PublicSkillComparison,
  type PublicSkillFilePreview,
  type PublicSkillInstallDescriptor,
  type PublicSkillInspection,
  type PublicSkillGitHubUpdate,
  type PublicSkillVersionDetail,
  type PublicSkillVersionSummary,
  type PublicTeamSkillSource,
} from "../../lib/public-skills-client";
import { MarketingFooter, MarketingHeader } from "../public/MarketingChrome";
import { DoorwayBrandIcon } from "../public/DoorwayBrandIcon";
import { FolderPicker } from "../upload/FolderPicker";

export interface PublicSkillsLibraryApi {
  loadCatalog: typeof loadPublicSkillCatalog;
  loadProfile: typeof loadPublicSkillProfile;
  loadSkill: typeof loadPublicSkill;
  loadVersions?: typeof loadPublicSkillVersions;
  loadVersion?: typeof loadPublicSkillVersion;
  loadPreview?: typeof loadPublicSkillPreview;
  loadComparison?: typeof loadPublicSkillComparison;
  loadInstall?: typeof loadPublicSkillInstall;
  rateSkill: typeof ratePublicSkill;
}

const defaultApi: PublicSkillsLibraryApi = {
  loadCatalog: loadPublicSkillCatalog,
  loadProfile: loadPublicSkillProfile,
  loadSkill: loadPublicSkill,
  loadVersions: loadPublicSkillVersions,
  loadVersion: loadPublicSkillVersion,
  loadPreview: loadPublicSkillPreview,
  loadComparison: loadPublicSkillComparison,
  loadInstall: loadPublicSkillInstall,
  rateSkill: ratePublicSkill,
};

export function PublicSkillsLibraryPage({ api = defaultApi }: { api?: PublicSkillsLibraryApi }) {
  const [catalog, setCatalog] = useState<PublicSkillCatalog | null>(null);
  const [filters, setFilters] = useState(() => {
    const query = new URLSearchParams(window.location.search);
    return {
      query: query.get("query") ?? "",
      theme: query.get("theme") ?? "",
      tag: query.get("tag") ?? "",
      sort: query.get("sort") ?? "featured",
    };
  });
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const timeout = window.setTimeout(() => {
      void api.loadCatalog(filters).then((result) => {
        if (!active) return;
        setCatalog(result);
        setError("");
      }).catch((cause: unknown) => {
        if (active) setError(message(cause, "The library could not load."));
      });
    }, filters.query ? 180 : 0);
    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [api, filters]);

  useLibraryMetadata(
    "Claude and Codex Skills Library",
    "Find reusable marketing skills for Claude Code and Codex, shared by the people who use them.",
    "/library",
  );

  return (
    <LibraryShell>
      <main className="library-main">
        <section className="library-hero">
          <div className="library-hero-copy">
            <p className="eyebrow"><span className="library-live-dot" aria-hidden="true" />Public skills, ready to borrow</p>
            <h1>Borrow a better way to work.</h1>
            <p>Find reusable Claude and Codex workflows made for the jobs marketers do every day. Download one. Make it yours. Share the next good one back.</p>
            <div className="library-hero-actions">
              <a className="primary-button" href="/manage/library">Publish a skill <span aria-hidden="true">↗</span></a>
              <a className="secondary-button" href="#browse-skills">Browse the library</a>
            </div>
          </div>
          <LibraryPassportVisual />
        </section>

        <section className="library-browser" id="browse-skills" aria-labelledby="library-heading">
          <div className="library-browser-heading">
            <div><p className="eyebrow">The collection</p><h2 id="library-heading">Find the workflow you need next.</h2></div>
            <label className="library-search">
              <span className="visually-hidden">Search skills</span>
              <i aria-hidden="true">⌕</i>
              <input
                type="search"
                value={filters.query}
                placeholder="Search jobs, skills, or authors"
                onChange={(event) => setFilters((current) => ({ ...current, query: event.target.value }))}
              />
            </label>
          </div>
          <div className="library-toolbar">
            <div className="library-theme-chips" aria-label="Filter by theme">
              <button type="button" aria-pressed={!filters.theme} onClick={() => setFilters((current) => ({ ...current, theme: "" }))}>All skills</button>
              {(catalog?.themes ?? []).map((theme) => (
                <button
                  type="button"
                  key={theme.key}
                  aria-pressed={filters.theme === theme.key}
                  onClick={() => setFilters((current) => ({ ...current, theme: theme.key }))}
                >
                  {theme.name}
                </button>
              ))}
            </div>
            <label className="library-sort">
              <span>Sort</span>
              <select value={filters.sort} onChange={(event) => setFilters((current) => ({ ...current, sort: event.target.value }))}>
                <option value="featured">Featured</option>
                <option value="downloads">Most downloaded</option>
                <option value="rating">Highest rated</option>
                <option value="newest">Newest</option>
              </select>
            </label>
          </div>

          {error && <p className="library-error" role="alert">{error}</p>}
          {!catalog && !error && <LibraryLoading />}
          {catalog?.skills.length === 0 && <LibraryEmpty query={filters.query} />}
          {catalog && catalog.skills.length > 0 && (
            <div className="library-skill-grid">
              {catalog.skills.map((skill, index) => <SkillPassportCard skill={skill} rank={index + 1} key={skill.id} />)}
            </div>
          )}
        </section>

        <section className="library-loop">
          <DoorwayBrandIcon name="door-solid-green" />
          <div><p className="eyebrow">Made something worth repeating?</p><h2>Put your best workflow on the shelf.</h2></div>
          <a className="primary-button" href="/manage/library">Publish your skill</a>
        </section>
      </main>
    </LibraryShell>
  );
}

export function PublicSkillAuthorPage({ handle, api = defaultApi }: {
  handle: string;
  api?: PublicSkillsLibraryApi;
}) {
  const [result, setResult] = useState<Awaited<ReturnType<typeof loadPublicSkillProfile>> | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    void api.loadProfile(handle).then((value) => {
      if (active) {
        setResult(value);
        if (window.location.pathname !== value.canonicalPath) {
          window.history.replaceState(null, "", value.canonicalPath);
        }
      }
    }).catch((cause: unknown) => {
      if (active) setError(message(cause, "That author profile could not load."));
    });
    return () => { active = false; };
  }, [api, handle]);
  useLibraryMetadata(
    result ? `${result.profile.displayName}'s Claude and Codex skills` : "Skills Library author",
    result?.profile.bio || "Browse reusable Claude and Codex skills shared through Doorway.",
    `/library/${handle}`,
  );

  return (
    <LibraryShell>
      <main className="library-main library-author-main">
        {error && <LibraryState title="Author unavailable" copy={error} />}
        {!result && !error && <LibraryLoading />}
        {result && (
          <>
            <section className="library-author-hero">
              <AuthorAvatar profile={result.profile} />
              <div>
                <p className="eyebrow">{result.profile.status === "unclaimed" ? "Unclaimed contributor" : "Doorway author"}</p>
                <h1>{result.profile.displayName}</h1>
                <p>{result.profile.bio || "Reusable workflows shared with the Doorway community."}</p>
                <div className="library-author-links">
                  <span>@{result.profile.handle}</span>
                  {result.profile.websiteUrl && <a href={result.profile.websiteUrl} target="_blank" rel="noreferrer">Website ↗</a>}
                </div>
              </div>
            </section>
            <section className="library-author-skills" aria-labelledby="author-skills-heading">
              <div className="library-section-heading">
                <p className="eyebrow">Published shelf</p>
                <h2 id="author-skills-heading">{result.skills.length} {result.skills.length === 1 ? "skill" : "skills"} by {result.profile.displayName}</h2>
              </div>
              {result.skills.length
                ? <div className="library-skill-grid">{result.skills.map((skill, index) => <SkillPassportCard skill={skill} rank={index + 1} key={skill.id} />)}</div>
                : <LibraryEmpty query="" />}
            </section>
          </>
        )}
      </main>
    </LibraryShell>
  );
}

export function PublicSkillDetailPage({ handle, slug, api = defaultApi }: {
  handle: string;
  slug: string;
  api?: PublicSkillsLibraryApi;
}) {
  const [result, setResult] = useState<Awaited<ReturnType<typeof loadPublicSkill>> | null>(null);
  const [error, setError] = useState("");
  const [ratingMessage, setRatingMessage] = useState("");
  const [versionDetail, setVersionDetail] = useState<PublicSkillVersionDetail | null>(null);
  const [versions, setVersions] = useState<PublicSkillVersionSummary[]>([]);
  const [preview, setPreview] = useState<PublicSkillFilePreview | null>(null);
  const [comparison, setComparison] = useState<PublicSkillComparison | null>(null);
  const [install, setInstall] = useState<PublicSkillInstallDescriptor | null>(null);
  const [detailError, setDetailError] = useState("");
  const [selectedClient, setSelectedClient] = useState<"claude" | "codex" | "zip">("claude");
  const [previewCopyMessage, setPreviewCopyMessage] = useState("");
  const [copyMessage, setCopyMessage] = useState("");
  const [showComparison, setShowComparison] = useState(false);
  const selectedInstallClient = install?.clients.find((client) => client.id === selectedClient);
  const addedCapabilityEntries = comparison ? capabilityEntries(comparison.capabilities.added) : [];
  const removedCapabilityEntries = comparison ? capabilityEntries(comparison.capabilities.removed) : [];
  useEffect(() => {
    let active = true;
    void api.loadSkill(handle, slug).then((value) => {
      if (active) {
        setResult(value);
        if (window.location.pathname !== value.canonicalPath) {
          window.history.replaceState(null, "", value.canonicalPath);
        }
      }
    }).catch((cause: unknown) => {
      if (active) setError(message(cause, "That skill could not load."));
    });
    return () => { active = false; };
  }, [api, handle, slug]);
  useEffect(() => {
    if (!result || !api.loadVersions || !api.loadVersion || !api.loadPreview || !api.loadInstall) return;
    let active = true;
    const { id: skillId, version } = result.skill;
    void Promise.all([
      api.loadVersions(skillId),
      api.loadVersion(skillId, version.id),
      api.loadPreview(skillId, version.id, "SKILL.md"),
      api.loadInstall(skillId, version.id),
    ]).then(async ([history, detail, file, installResult]) => {
      if (!active) return;
      setVersions(history.versions);
      setVersionDetail(detail.version);
      setPreview(file.file);
      setInstall(installResult.install);
      if (active) setDetailError("");
    }).catch((cause: unknown) => {
      if (active) setDetailError(message(cause, "Package details could not load."));
    });
    return () => { active = false; };
  }, [api, result]);
  useLibraryMetadata(
    result ? `${result.skill.displayName} by ${result.skill.author.displayName}` : "Skills Library skill",
    result?.skill.summary || "Download a reusable Claude or Codex skill from Doorway.",
    `/library/${handle}/${slug}`,
  );

  return (
    <LibraryShell>
      <main className="library-main library-detail-main">
        {error && <LibraryState title="Skill unavailable" copy={error} />}
        {!result && !error && <LibraryLoading />}
        {result && (
          <>
            <nav className="library-breadcrumb" aria-label="Breadcrumb">
              <a href="/library">Library</a><span>/</span><a href={`/library/${result.skill.author.handle}`}>{result.skill.author.displayName}</a>
            </nav>
            <article className="library-detail-card">
              <div className="library-detail-copy">
                <div className="library-detail-labels">
                  {result.skill.theme && <span>{result.skill.theme.name}</span>}
                  {result.skill.featured && <span>Featured</span>}
                  <span>v{result.skill.version.number}</span>
                </div>
                <h1>{result.skill.displayName}</h1>
                <p className="library-detail-summary">{result.skill.summary}</p>
                <a className="library-detail-author" href={`/library/${result.skill.author.handle}`}>
                  <AuthorAvatar profile={{
                    id: result.skill.author.id,
                    handle: result.skill.author.handle,
                    displayName: result.skill.author.displayName,
                    bio: result.skill.author.bio,
                    imageUrl: result.skill.author.imageUrl,
                    websiteUrl: result.skill.author.websiteUrl,
                    status: result.skill.author.claimed ? "claimed" : "unclaimed",
                  }} compact />
                  <span><small>Shared by</small><strong>{result.skill.author.displayName}</strong></span>
                  {!result.skill.author.claimed && <em>Unclaimed</em>}
                </a>
                <div className="library-detail-actions">
                  <a className="primary-button" href={`/api/library/v1/skills/${result.skill.id}/download`}>
                    Download skill <span aria-hidden="true">↓</span>
                  </a>
                  <a
                    className="secondary-button"
                    href={`/manage/library?${new URLSearchParams({
                      remixSkillId: result.skill.id,
                      remixVersionId: result.skill.version.id,
                      name: `Copy of ${result.skill.displayName}`,
                      summary: result.skill.summary,
                      tags: result.skill.tags.join(", "),
                    })}`}
                  >
                    Remix this skill
                  </a>
                  <span>{result.skill.downloads.toLocaleString()} downloads</span>
                </div>
              </div>
              <aside className="library-skill-passport">
                <p>Skill passport</p>
                <dl>
                  <div><dt>Capability</dt><dd>{capabilityLabel(result.skill.capability)}</dd></div>
                  <div><dt>Works with</dt><dd>{result.skill.compatibility.length ? result.skill.compatibility.join(", ") : "Standard Agent Skills"}</dd></div>
                  <div><dt>Version</dt><dd>{result.skill.version.number}</dd></div>
                  <div><dt>Rating</dt><dd>{result.skill.rating.average ? `${result.skill.rating.average} / 5` : "New"}</dd></div>
                </dl>
                <div className="library-passport-stamp"><DoorwayBrandIcon name="door-outline-green" /><span>Checked by Doorway</span></div>
              </aside>
            </article>

            <section className="library-detail-lower">
              <div className="library-detail-tags">
                <p className="eyebrow">Useful for</p>
                <div>{result.skill.tags.map((tag) => <a href={`/library?tag=${encodeURIComponent(tag.toLowerCase())}`} key={tag}>{tag}</a>)}</div>
              </div>
              <div className="library-rating-panel">
                <div><p className="eyebrow">Used it?</p><h2>Help the next marketer choose.</h2></div>
                <div className="library-stars" aria-label="Rate this skill">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      type="button"
                      aria-label={`Rate ${rating} ${rating === 1 ? "star" : "stars"}`}
                      key={rating}
                      onClick={() => {
                        setRatingMessage("Saving…");
                        void api.rateSkill(result.skill.id, rating).then(() => {
                          setRatingMessage("Thanks. Your rating is live.");
                        }).catch(() => {
                          setRatingMessage("Sign in to Doorway to rate this skill.");
                        });
                      }}
                    >
                      ★
                    </button>
                  ))}
                </div>
                <p aria-live="polite">{ratingMessage || (
                  result.skill.rating.average
                    ? `${result.skill.rating.average} from ${result.skill.rating.count} ratings`
                    : "Be the first to rate it."
                )}</p>
              </div>
            </section>
            {detailError && <p className="library-error" role="alert">{detailError}</p>}
            {versionDetail && preview && (
              <section className="library-package-review" aria-labelledby="package-review-heading">
                <div className="library-section-heading">
                  <p className="eyebrow">Inside the skill</p>
                  <h2 id="package-review-heading">Read it before you install it.</h2>
                  <p>Doorway keeps the package visible, so you can understand the workflow and its requirements before adding it to an agent.</p>
                </div>
                <div className="library-package-grid">
                  <article className="library-skill-preview">
                    <div className="library-package-card-heading">
                      <div><span>Primary instructions</span><strong>{preview.path}</strong></div>
                      <div className="library-package-card-actions">
                        <small>{formatBytes(preview.size)}</small>
                        {preview.text && (
                          <button
                            type="button"
                            aria-label={`Copy ${preview.path} instructions`}
                            onClick={() => {
                              if (!navigator.clipboard) {
                                setPreviewCopyMessage("Could not copy. Select the instructions manually.");
                                return;
                              }
                              void navigator.clipboard.writeText(preview.text ?? "")
                                .then(() => setPreviewCopyMessage("Copied."))
                                .catch(() => setPreviewCopyMessage("Could not copy. Select the instructions manually."));
                            }}
                          >
                            {previewCopyMessage === "Copied." ? "Copied" : "Copy"}
                          </button>
                        )}
                      </div>
                    </div>
                    <pre dir="auto">{preview.text ?? "A safe text preview is unavailable for this file."}</pre>
                    <p className="visually-hidden" role="status" aria-live="polite">{previewCopyMessage}</p>
                    {preview.truncated && <p>Preview shortened: {preview.truncationReason ?? "display limit reached"}.</p>}
                  </article>
                  <aside className="library-file-tree">
                    <div className="library-package-card-heading">
                      <div><span>Complete package</span><strong>{versionDetail.files.length} files</strong></div>
                      <small>{formatBytes(versionDetail.archiveBytes)}</small>
                    </div>
                    <ul>{versionDetail.files.map((file) => (
                      <li key={file.path}><span>{file.path}</span><small>{formatBytes(file.size)}</small></li>
                    ))}</ul>
                  </aside>
                </div>
              </section>
            )}
            {versionDetail && (
              <section className="library-trust-grid">
                <article className="library-trust-card">
                  <p className="eyebrow">Automated package review</p>
                  <h2>What Doorway checked</h2>
                  <p>Doorway validated the folder structure, scanned for likely secrets, classified capabilities, and recorded the immutable archive digest. This is an automated package check, not a human security audit.</p>
                  <ul>
                    <li><span>Package validation</span><strong>{versionDetail.checks.validationResult}</strong></li>
                    <li><span>Capability</span><strong>{capabilityLabel(String(versionDetail.capability.classification ?? result.skill.capability))}</strong></li>
                    <li><span>Archive digest</span><code>{shortDigest(versionDetail.archiveSha256)}</code></li>
                  </ul>
                </article>
                <article className="library-trust-card">
                  <p className="eyebrow">Where it came from</p>
                  <h2>{provenanceTitle(versionDetail.provenance.sourceType)}</h2>
                  <p>{provenanceDescription(versionDetail)}</p>
                  {versionDetail.provenance.repositoryUrl && (
                    <a href={versionDetail.provenance.repositoryUrl} rel="noreferrer" target="_blank">Open the GitHub source ↗</a>
                  )}
                  {versionDetail.provenance.resolvedSha && (
                    <p className="library-provenance-sha">Commit <code>{versionDetail.provenance.resolvedSha.slice(0, 7)}</code></p>
                  )}
                  {versionDetail.provenance.parentPath && (
                    <a href={versionDetail.provenance.parentPath}>
                      Remixed from {versionDetail.provenance.parentDisplayName ?? "this Doorway skill"}
                    </a>
                  )}
                </article>
              </section>
            )}
            {install && (
              <section className="library-install-panel" aria-labelledby="install-heading">
                <div>
                  <p className="eyebrow">Choose your agent</p>
                  <h2 id="install-heading">Install version {install.versionNumber}</h2>
                  <p>Each option points to this exact immutable version. You stay in control of the final folder permission.</p>
                </div>
                <div className="library-install-clients" role="group" aria-label="Install target">
                  {install.clients.map((client) => (
                    <button
                      type="button"
                      aria-pressed={selectedClient === client.id}
                      onClick={() => {
                        setSelectedClient(client.id);
                        setCopyMessage("");
                      }}
                      key={client.id}
                    >
                      {client.label}
                    </button>
                  ))}
                </div>
                {selectedInstallClient && (
                  <div className="library-install-instruction">
                    <p>{selectedInstallClient.instruction}</p>
                    {selectedInstallClient.command ? (
                      <div>
                        <code>{selectedInstallClient.command}</code>
                        <button type="button" onClick={() => {
                          void navigator.clipboard?.writeText(selectedInstallClient.command ?? "")
                            .then(() => setCopyMessage("Copied."))
                            .catch(() => setCopyMessage("Could not copy. Select the command manually."));
                        }}>Copy command</button>
                      </div>
                    ) : (
                      <a className="primary-button" href={install.downloadUrl}>Download ZIP</a>
                    )}
                  </div>
                )}
                <p className="library-copy-status" aria-live="polite">{copyMessage}</p>
                <small>SHA-256 {install.archiveSha256}</small>
              </section>
            )}
            {versionDetail?.baseVersionId && !showComparison && (
              <button
                className="secondary-button library-show-comparison"
                type="button"
                onClick={() => {
                  if (!result || !api.loadComparison) return;
                  setShowComparison(true);
                  void api.loadComparison(
                    result.skill.id,
                    versionDetail.id,
                    versionDetail.baseVersionId!,
                  ).then((value) => setComparison(value.comparison)).catch((cause: unknown) => {
                    setDetailError(message(cause, "The version comparison could not load."));
                  });
                }}
              >
                See what changed
              </button>
            )}
            {comparison && (
              <section className="library-change-panel" aria-labelledby="changes-heading">
                <div className="library-section-heading">
                  <p className="eyebrow">Version {comparison.base.number} to {comparison.target.number}</p>
                  <h2 id="changes-heading">What changed?</h2>
                  <p>{comparison.summary}</p>
                </div>
                {comparison.capabilities.changed && (
                  <div className="library-capability-changes">
                    <strong>Capability changes</strong>
                    {addedCapabilityEntries.map((item) => <span key={`cap-added-${item}`}>Added {item}</span>)}
                    {removedCapabilityEntries.map((item) => <span key={`cap-removed-${item}`}>Removed {item}</span>)}
                    {addedCapabilityEntries.length === 0
                      && removedCapabilityEntries.length === 0
                      && <span>Classification changed</span>}
                  </div>
                )}
                <ul className="library-change-files">{comparison.files.filter((file) => file.status !== "unchanged").map((file) => (
                  <li key={file.path}>
                    <span className={`library-file-status library-file-status-${file.status}`}>{statusLabel(file.status)}</span>
                    <code>{file.path}</code>
                    {file.textDiff?.lines && <pre dir="auto">{file.textDiff.lines.map((line) => `${line.type === "added" ? "+" : line.type === "removed" ? "-" : " "} ${line.text}`).join("\n")}</pre>}
                  </li>
                ))}</ul>
              </section>
            )}
            {versions.length > 0 && (
              <section className="library-version-history" aria-labelledby="version-history-heading">
                <div className="library-section-heading"><p className="eyebrow">Immutable history</p><h2 id="version-history-heading">Every published version</h2></div>
                <ol>{versions.map((version) => (
                  <li key={version.id}>
                    <div><strong>Version {version.number}</strong><span>{version.label}</span></div>
                    <p>{version.releaseNotes || "Package snapshot preserved by Doorway."}</p>
                    <small>{new Date(version.createdAt).toLocaleDateString()} · {version.fileCount} files · {shortDigest(version.archiveSha256)}</small>
                  </li>
                ))}</ol>
              </section>
            )}
          </>
        )}
      </main>
    </LibraryShell>
  );
}

export interface PublicSkillPublisherApi {
  loadProfile: typeof loadMyPublicSkillProfile;
  loadSkills: typeof loadMyPublicSkills;
  loadCatalog?: typeof loadPublicSkillCatalog;
  inspectSkill?: typeof inspectPublicSkill;
  publishSkill: typeof publishPublicSkill;
}

const defaultPublisherApi: PublicSkillPublisherApi = {
  loadProfile: loadMyPublicSkillProfile,
  loadSkills: loadMyPublicSkills,
  loadCatalog: loadPublicSkillCatalog,
  inspectSkill: inspectPublicSkill,
  publishSkill: publishPublicSkill,
};

export function PublicSkillPublisherPage({
  api = defaultPublisherApi,
}: {
  api?: PublicSkillPublisherApi;
} = {}) {
  const suggestedSkill = useMemo(() => {
    const query = new URLSearchParams(window.location.search);
    const workspaceId = query.get("workspaceId");
    const skillId = query.get("skillId");
    const versionId = query.get("versionId");
    const versionNumber = Number(query.get("versionNumber"));
    const sourceSummary = query.get("summary") ?? "";
    const remixSkillId = query.get("remixSkillId");
    const remixVersionId = query.get("remixVersionId");
    return {
      displayName: truncateSuggestedText(query.get("name") ?? "", 120),
      slug: query.get("slug") ?? "",
      summary: truncateSuggestedText(sourceSummary, 280),
      summaryWasShortened: sourceSummary.length > 280,
      tags: query.get("tags") ?? "",
      teamSkillSource: workspaceId && skillId && versionId && Number.isSafeInteger(versionNumber) && versionNumber > 0
        ? { workspaceId, skillId, versionId, versionNumber } satisfies PublicTeamSkillSource
        : undefined,
      remixSource: remixSkillId && remixVersionId
        ? { skillId: remixSkillId, versionId: remixVersionId }
        : undefined,
    };
  }, []);
  const [profile, setProfile] = useState<PublicSkillProfile | null | undefined>(undefined);
  const [profileError, setProfileError] = useState("");
  const [publishError, setPublishError] = useState("");
  const [publishedPath, setPublishedPath] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [teamSkillSource, setTeamSkillSource] = useState<PublicTeamSkillSource | undefined>(
    suggestedSkill.teamSkillSource,
  );
  const [publishing, setPublishing] = useState(false);
  const [sourceMode, setSourceMode] = useState<"folder" | "team" | "github">(
    suggestedSkill.teamSkillSource ? "team" : "folder",
  );
  const [githubRepository, setGithubRepository] = useState("");
  const [githubRef, setGithubRef] = useState("main");
  const [githubPath, setGithubPath] = useState("");
  const [inspection, setInspection] = useState<PublicSkillInspection | null>(null);
  const [inspecting, setInspecting] = useState(false);
  const [proposal, setProposal] = useState({
    displayName: suggestedSkill.displayName,
    slug: suggestedSkill.slug,
    summary: suggestedSkill.summary,
    themeKey: "",
    tags: suggestedSkill.tags,
  });
  const [ownedSkills, setOwnedSkills] = useState<OwnedPublicSkill[]>([]);
  const [ownedSkillsError, setOwnedSkillsError] = useState("");
  const [themes, setThemes] = useState<PublicSkillCatalog["themes"]>([]);
  const inspectionRequest = useRef(0);

  useEffect(() => {
    inspectionRequest.current += 1;
    setInspection(null);
    setInspecting(false);
    setPublishedPath("");
  }, [sourceMode, files, teamSkillSource, githubRepository, githubRef, githubPath]);

  const refreshOwnedSkills = useCallback(async () => {
    const skills = await api.loadSkills();
    setOwnedSkills(skills);
    setOwnedSkillsError("");
  }, [api]);

  useEffect(() => {
    let active = true;
    void api.loadProfile().then((value) => {
      if (active) setProfile(value);
    }).catch((cause: unknown) => {
      if (active) setProfileError(message(cause, "Your public profile could not load."));
    });
    return () => { active = false; };
  }, [api]);

  useEffect(() => {
    if (!api.loadCatalog) return;
    let active = true;
    void api.loadCatalog({ sort: "featured" }).then((catalog) => {
      if (active) setThemes(catalog.themes);
    }).catch(() => {
      if (active) setThemes([]);
    });
    return () => { active = false; };
  }, [api]);

  useEffect(() => {
    if (!profile) return;
    void refreshOwnedSkills().catch((cause: unknown) => {
      setOwnedSkillsError(message(cause, "Your published skills could not load."));
    });
  }, [profile, refreshOwnedSkills]);

  return (
    <div className="app-shell library-manage-shell">
      <header className="site-header library-manage-header">
        <a className="wordmark" href="/manage"><DoorwayBrandIcon className="oasis-wordmark-tile" name="logo-tile-green" />Doorway</a>
        <div className="header-actions"><a className="header-link" href="/library">View library</a><span className="demo-pill">Skills publisher</span></div>
      </header>
      <main className="library-manage-main">
        <section className="library-manage-heading">
          <p className="eyebrow">Public Skills Library</p>
          <h1>Share the workflow behind the work.</h1>
          <p>Choose a skill folder. Doorway checks it for secrets and unsupported files, then gives it a public page and a ZIP anyone can download.</p>
        </section>
        {profileError && <p className="library-error" role="alert">{profileError}</p>}
        {profile === undefined && !profileError && <LibraryLoading />}
        {profile === null && <ProfileSetup onSaved={setProfile} />}
        {profile && (
          <>
          <form className="library-publish-form" onSubmit={(event) => {
            event.preventDefault();
            setPublishError("");
            setPublishing(true);
            void api.publishSkill({
              slug: proposal.slug,
              displayName: proposal.displayName,
              summary: proposal.summary,
              themeKey: proposal.themeKey,
              tags: proposal.tags.split(",").map((tag) => tag.trim()).filter(Boolean),
              files,
              ...(inspection ? { inspectionId: inspection.id } : {}),
              ...(!inspection && teamSkillSource ? { teamSkillSource } : {}),
              ...(suggestedSkill.remixSource ? { remixSource: suggestedSkill.remixSource } : {}),
            }).then((skill) => {
              setPublishedPath(skill.path);
              setInspection(null);
              void refreshOwnedSkills();
            }).catch((cause: unknown) => {
              setPublishError(message(cause, "Doorway could not publish this skill."));
            }).finally(() => setPublishing(false));
          }}>
            <div className="library-publisher-profile">
              <AuthorAvatar profile={profile} compact />
              <div><small>Publishing as</small><strong>{profile.displayName}</strong><span>meetdoorway.com/library/{profile.handle}</span></div>
            </div>
            {suggestedSkill.remixSource && (
              <p className="library-remix-notice">
                This publication will credit the exact Doorway skill version you started from.
              </p>
            )}
            <div className="library-source-picker" role="group" aria-label="Skill source">
              <button type="button" aria-pressed={sourceMode === "folder"} onClick={() => {
                setSourceMode("folder");
                setInspection(null);
              }}>Choose a folder</button>
              <button type="button" aria-pressed={sourceMode === "github"} onClick={() => {
                setSourceMode("github");
                setTeamSkillSource(undefined);
                setFiles([]);
                setInspection(null);
              }}>Import from GitHub</button>
              {teamSkillSource && <button type="button" aria-pressed={sourceMode === "team"} onClick={() => setSourceMode("team")}>Use Team Skill</button>}
            </div>
            {sourceMode === "github" && (
              <div className="library-github-source">
                <label><span>GitHub repository</span><input aria-label="GitHub repository" type="url" value={githubRepository} onChange={(event) => {
                  setGithubRepository(event.target.value);
                  setInspection(null);
                }} placeholder="https://github.com/owner/repository" /></label>
                <label><span>Branch or tag</span><input aria-label="Branch or tag" value={githubRef} onChange={(event) => {
                  setGithubRef(event.target.value);
                  setInspection(null);
                }} /></label>
                <label><span>Repository folder</span><input aria-label="Repository folder" value={githubPath} onChange={(event) => {
                  setGithubPath(event.target.value);
                  setInspection(null);
                }} placeholder="skills/customer-research" /></label>
              </div>
            )}
            {(sourceMode === "folder" || sourceMode === "team") && (
              <SkillFolderField
                files={files}
                {...(sourceMode === "team" && teamSkillSource ? { teamSkillSource } : {})}
                onFiles={(selected) => {
                  setFiles(selected);
                  setSourceMode("folder");
                  setTeamSkillSource(undefined);
                  setInspection(null);
                }}
              />
            )}
            {api.inspectSkill && (
              <button
                className="secondary-button library-inspect-button"
                type="button"
                disabled={inspecting || (sourceMode === "folder" && files.length === 0) || (sourceMode === "team" && !teamSkillSource) || (sourceMode === "github" && !githubRepository)}
                onClick={() => {
                  const requestId = ++inspectionRequest.current;
                  setInspecting(true);
                  setPublishError("");
                  const input = sourceMode === "github"
                    ? {
                        githubSource: {
                          repositoryUrl: githubRepository,
                          ...(githubRef ? { ref: githubRef } : {}),
                          ...(githubPath ? { path: githubPath } : {}),
                        },
                      }
                    : sourceMode === "team" && teamSkillSource
                      ? { teamSkillSource }
                      : { files };
                  void api.inspectSkill!(input).then((result) => {
                    if (requestId !== inspectionRequest.current) return;
                    setInspection(result);
                    setProposal({
                      displayName: result.proposed.displayName,
                      slug: result.proposed.slug,
                      summary: result.proposed.summary,
                      themeKey: result.proposed.themeKey ?? "",
                      tags: result.proposed.tags.join(", "),
                    });
                  }).catch((cause: unknown) => {
                    if (requestId !== inspectionRequest.current) return;
                    setPublishError(message(cause, "Doorway could not inspect this source."));
                  }).finally(() => {
                    if (requestId === inspectionRequest.current) setInspecting(false);
                  });
                }}
              >
                {inspecting ? "Reviewing source…" : "Review this source"}
              </button>
            )}
            {inspection && (
              <div className="library-inspection-summary" role="status">
                <div>
                  <strong>{inspection.files.length} {inspection.files.length === 1 ? "file" : "files"} ready</strong>
                  {inspection.source.type === "github" && inspection.source.resolvedSha && (
                    <span>Pinned to commit {inspection.source.resolvedSha.slice(0, 7)}</span>
                  )}
                </div>
                <ul>{inspection.checks.map((check) => <li key={check.key}>{check.label} {check.result}</li>)}</ul>
              </div>
            )}
            <div className="library-publish-grid">
              <label><span>Skill name</span><input aria-label="Skill name" name="displayName" required maxLength={120} placeholder="Customer Research Brief" value={proposal.displayName} onChange={(event) => setProposal((current) => ({ ...current, displayName: event.target.value }))} /></label>
              <label><span>Public URL</span><div className="library-slug-field"><small>/{profile.handle}/</small><input name="slug" required pattern="[a-z0-9-]+" placeholder="customer-research" value={proposal.slug} onChange={(event) => setProposal((current) => ({ ...current, slug: event.target.value }))} /></div></label>
              <label className="library-publish-wide">
                <span>What does it help someone do?</span>
                <textarea aria-label="What does it help someone do?" name="summary" required maxLength={280} rows={3} placeholder="Turn raw customer notes into a clear, cited insight brief." value={proposal.summary} onChange={(event) => setProposal((current) => ({ ...current, summary: event.target.value }))} />
                {suggestedSkill.summaryWasShortened && <small>Shortened from the Team Skill description to fit the 280-character public summary limit.</small>}
              </label>
              <label><span>Theme</span>{themes.length ? (
                <select name="themeKey" value={proposal.themeKey} onChange={(event) => setProposal((current) => ({ ...current, themeKey: event.target.value }))}>
                  <option value="">Choose a theme</option>
                  {themes.map((theme) => <option value={theme.key} key={theme.key}>{theme.name}</option>)}
                </select>
              ) : <input name="themeKey" placeholder="research" value={proposal.themeKey} onChange={(event) => setProposal((current) => ({ ...current, themeKey: event.target.value }))} />}</label>
              <label><span>Tags</span><input name="tags" placeholder="positioning, voice-of-customer" value={proposal.tags} onChange={(event) => setProposal((current) => ({ ...current, tags: event.target.value }))} /><small>Separate up to eight tags with commas.</small></label>
            </div>
            {publishError && <p className="library-error" role="alert">{publishError}</p>}
            {publishedPath && <p className="library-publish-success">Published. <a href={publishedPath}>Open the public skill page ↗</a></p>}
            <button className="primary-button library-publish-button" type="submit" disabled={publishing || (api.inspectSkill ? !inspection : (!teamSkillSource && files.length === 0))}>
              {publishing ? "Checking and publishing…" : "Publish skill"}
            </button>
          </form>
          <ProfileEditor profile={profile} onSaved={setProfile} />
          <OwnedSkillsManager
            profile={profile}
            skills={ownedSkills}
            error={ownedSkillsError}
            onChanged={refreshOwnedSkills}
          />
          </>
        )}
      </main>
    </div>
  );
}

function ProfileEditor({ profile, onSaved }: {
  profile: PublicSkillProfile;
  onSaved: (profile: PublicSkillProfile) => void;
}) {
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  return (
    <details className="library-owner-panel">
      <summary>Edit your author page</summary>
      <form onSubmit={(event) => {
        event.preventDefault();
        const body = new FormData(event.currentTarget);
        setSaving(true);
        setError("");
        void saveMyPublicSkillProfile({
          handle: String(body.get("handle") ?? ""),
          displayName: String(body.get("displayName") ?? ""),
          bio: String(body.get("bio") ?? ""),
          imageUrl: String(body.get("imageUrl") ?? ""),
          websiteUrl: String(body.get("websiteUrl") ?? ""),
        }).then(onSaved).catch((cause: unknown) => {
          setError(message(cause, "Your author page could not be updated."));
        }).finally(() => setSaving(false));
      }}>
        <div className="library-publish-grid">
          <label><span>Public name</span><input name="displayName" required defaultValue={profile.displayName} /></label>
          <label><span>Handle</span><div className="library-slug-field"><small>/library/</small><input name="handle" required pattern="[a-z0-9-]+" defaultValue={profile.handle} /></div></label>
          <label className="library-publish-wide"><span>Short bio</span><textarea name="bio" rows={3} defaultValue={profile.bio} /></label>
          <label><span>Profile image URL</span><input name="imageUrl" type="url" defaultValue={profile.imageUrl ?? ""} placeholder="https://" /></label>
          <label><span>Website</span><input name="websiteUrl" type="url" defaultValue={profile.websiteUrl ?? ""} placeholder="https://" /></label>
        </div>
        {error && <p className="library-error" role="alert">{error}</p>}
        <button className="primary-button" type="submit" disabled={saving}>{saving ? "Saving…" : "Save author page"}</button>
      </form>
    </details>
  );
}

function OwnedSkillsManager({ profile, skills, error, onChanged }: {
  profile: PublicSkillProfile;
  skills: OwnedPublicSkill[];
  error: string;
  onChanged: () => Promise<void>;
}) {
  return (
    <section className="library-owner-skills" aria-labelledby="owned-skills-heading">
      <div className="library-section-heading">
        <p className="eyebrow">Your shelf</p>
        <h2 id="owned-skills-heading">Keep every skill current.</h2>
      </div>
      {error && <p className="library-error" role="alert">{error}</p>}
      {!error && skills.length === 0 && <p className="library-owner-empty">Your first published skill will appear here.</p>}
      <div className="library-owner-skill-list">
        {skills.map((skill) => (
          <OwnedSkillEditor profile={profile} skill={skill} onChanged={onChanged} key={skill.skill_id} />
        ))}
      </div>
    </section>
  );
}

function OwnedSkillEditor({ profile, skill, onChanged }: {
  profile: PublicSkillProfile;
  skill: OwnedPublicSkill;
  onChanged: () => Promise<void>;
}) {
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [githubUpdate, setGithubUpdate] = useState<PublicSkillGitHubUpdate | null>(null);
  const tags = parsedTags(skill.tags_json).join(", ");
  useEffect(() => {
    setGithubUpdate(null);
  }, [skill.version_number]);
  const run = async (key: string, action: () => Promise<unknown>, success: string) => {
    if (busy) return;
    setBusy(key);
    setNotice("");
    setError("");
    try {
      await action();
      setNotice(success);
      if (key === "version") setFiles([]);
      try {
        await onChanged();
      } catch {
        setError("The change succeeded, but this list could not refresh. Reload to see the latest version.");
      }
    } catch (cause) {
      setError(message(cause, "Doorway could not update this skill."));
    } finally {
      setBusy("");
    }
  };
  return (
    <details className="library-owner-skill">
      <summary>
        <span><strong>{skill.display_name}</strong><small>v{skill.version_number} · {skill.status} · {Number(skill.download_count).toLocaleString()} downloads</small></span>
        <a href={`/library/${profile.handle}/${skill.slug}`} onClick={(event) => event.stopPropagation()}>View ↗</a>
      </summary>
      <div className="library-owner-skill-body">
        <form onSubmit={(event) => {
          event.preventDefault();
          const body = new FormData(event.currentTarget);
          void run("metadata", () => updateMyPublicSkill(skill.skill_id, {
            displayName: String(body.get("displayName") ?? ""),
            summary: String(body.get("summary") ?? ""),
            themeKey: String(body.get("themeKey") ?? "") || null,
            tags: String(body.get("tags") ?? "").split(",").map((tag) => tag.trim()).filter(Boolean),
            status: String(body.get("status")) as "draft" | "published" | "hidden",
          }), "Skill details saved.");
        }}>
          <div className="library-publish-grid">
            <label><span>Skill name</span><input name="displayName" required defaultValue={skill.display_name} /></label>
            <label><span>Status</span><select name="status" defaultValue={skill.status}><option value="published">Published</option><option value="hidden">Hidden</option><option value="draft">Draft</option></select></label>
            <label className="library-publish-wide"><span>Summary</span><textarea name="summary" required rows={3} defaultValue={skill.summary} /></label>
            <label><span>Theme key</span><input name="themeKey" defaultValue={skill.theme_key ?? ""} /></label>
            <label><span>Tags</span><input name="tags" defaultValue={tags} /></label>
          </div>
          <button className="secondary-button" type="submit" disabled={Boolean(busy)}>{busy === "metadata" ? "Saving…" : "Save details"}</button>
        </form>
        <div className="library-owner-version">
          <div><strong>Publish version {skill.version_number + 1}</strong><span>Choose the complete folder. The current download stays live until this version passes validation.</span></div>
          <SkillFolderField files={files} onFiles={setFiles} />
          <button
            className="primary-button"
            type="button"
            disabled={Boolean(busy) || files.length === 0}
            onClick={() => void run("version", () => publishPublicSkillVersion(skill.skill_id, files), `Version ${skill.version_number + 1} is live.`)}
          >
            {busy === "version" ? "Checking and publishing…" : "Publish new version"}
          </button>
        </div>
        {skill.source_type === "github" && (
          <div className="library-github-update">
            <div>
              <strong>GitHub source</strong>
              <span>Check the pinned repository for a newer commit. Checking never changes the live version.</span>
            </div>
            <button
              className="secondary-button"
              type="button"
              disabled={Boolean(busy)}
              onClick={() => void run("github-check", async () => {
                const update = await checkPublicSkillGitHubUpdate(skill.skill_id);
                setGithubUpdate(update);
              }, "GitHub check complete.")}
            >
              {busy === "github-check" ? "Checking…" : "Check GitHub for updates"}
            </button>
            {githubUpdate && (
              <div className="library-github-update-result">
                {githubUpdate.available ? (
                  <>
                    <p><strong>Update available</strong><span>Commit {githubUpdate.latest.resolvedSha.slice(0, 7)}</span></p>
                    {githubUpdate.comparison && (
                      <div>
                        <p>{githubUpdate.comparison.summary}</p>
                        <ul>{githubUpdate.comparison.files.filter((file) => file.status !== "unchanged").map((file) => (
                          <li key={file.path}><span className={`library-file-status library-file-status-${file.status}`}>{statusLabel(file.status)}</span><code>{file.path}</code></li>
                        ))}</ul>
                      </div>
                    )}
                    <button
                      className="primary-button"
                      type="button"
                      disabled={Boolean(busy)}
                      onClick={() => void run("github-apply", async () => {
                        await applyPublicSkillGitHubUpdate(
                          skill.skill_id,
                          githubUpdate.latest.resolvedSha,
                          githubUpdate.current.versionId,
                          githubUpdate.current.archiveSha256,
                          githubUpdate.comparison?.summary ?? "Reviewed GitHub source update.",
                        );
                        setGithubUpdate(null);
                      }, "The reviewed GitHub update is now a new immutable version.")}
                    >
                      {busy === "github-apply" ? "Publishing update…" : "Publish reviewed update"}
                    </button>
                  </>
                ) : <p><strong>Already current</strong><span>Doorway found no package changes to publish.</span></p>}
              </div>
            )}
          </div>
        )}
        {error && <p className="library-error" role="alert">{error}</p>}
        {notice && <p className="library-publish-success" role="status">{notice}</p>}
      </div>
    </details>
  );
}

export function PublicSkillClaimPage({ token }: { token: string }) {
  const [state, setState] = useState<"idle" | "claiming" | "complete" | "failed">("idle");
  const [profile, setProfile] = useState<PublicSkillProfile | null>(null);
  return (
    <div className="app-shell library-manage-shell">
      <header className="site-header library-manage-header"><a className="wordmark" href="/"><DoorwayBrandIcon className="oasis-wordmark-tile" name="logo-tile-green" />Doorway</a></header>
      <main className="library-claim-main">
        <section className="library-claim-card">
          <DoorwayBrandIcon name="door-outline-green" />
          <p className="eyebrow">Profile invitation</p>
          {state === "complete" && profile ? (
            <><h1>This shelf is yours.</h1><p>You now own @{profile.handle} and every skill already published on that profile.</p><a className="primary-button" href="/manage/library">Open your publisher</a></>
          ) : (
            <><h1>Claim your Doorway author page.</h1><p>Attach the invited profile and its public skills to the Doorway account you just signed in with.</p>
              {state === "failed" && <p className="library-error" role="alert">This claim link is expired, already used, or belongs to a profile that has been claimed.</p>}
              <button className="primary-button" type="button" disabled={!token || state === "claiming"} onClick={() => {
                setState("claiming");
                void acceptPublicSkillProfileClaim(token).then((value) => {
                  setProfile(value);
                  setState("complete");
                }).catch(() => setState("failed"));
              }}>{state === "claiming" ? "Claiming profile…" : "Claim this profile"}</button>
            </>
          )}
        </section>
      </main>
    </div>
  );
}

function LibraryShell({ children }: { children: React.ReactNode }) {
  return <div className="app-shell oasis-marketing-shell library-shell"><MarketingHeader current="library" />{children}<MarketingFooter /></div>;
}

function SkillPassportCard({ skill, rank }: { skill: PublicSkill; rank: number }) {
  return (
    <article className={`library-skill-card${skill.featured ? " library-skill-featured" : ""}`}>
      <div className="library-card-topline">
        <span>{String(rank).padStart(2, "0")}</span>
        {skill.featured && <em>Featured</em>}
        <small>v{skill.version.number}</small>
      </div>
      <div className="library-card-mark" aria-hidden="true"><DoorwayBrandIcon name="door-outline-green" /></div>
      <div className="library-card-taxonomy">
        {skill.theme && <span>{skill.theme.name}</span>}
        {skill.tags.slice(0, 2).map((tag) => <span key={tag}>{tag}</span>)}
      </div>
      <h3><a href={skill.path}>{skill.displayName}</a></h3>
      <p>{skill.summary}</p>
      <div className="library-card-author">
        <AuthorAvatar profile={{
          id: skill.author.id,
          handle: skill.author.handle,
          displayName: skill.author.displayName,
          bio: skill.author.bio,
          imageUrl: skill.author.imageUrl,
          websiteUrl: skill.author.websiteUrl,
          status: skill.author.claimed ? "claimed" : "unclaimed",
        }} compact />
        <a href={`/library/${skill.author.handle}`}>{skill.author.displayName}</a>
      </div>
      <div className="library-card-metrics">
        <span>{skill.downloads.toLocaleString()} downloads</span>
        <span>{skill.rating.average ? `${skill.rating.average} · ${skill.rating.count} ratings` : "New"}</span>
      </div>
      <a className="library-card-open" href={skill.path}>Open skill <span aria-hidden="true">→</span></a>
    </article>
  );
}

function AuthorAvatar({ profile, compact = false }: { profile: PublicSkillProfile; compact?: boolean }) {
  return profile.imageUrl
    ? <img className={`library-avatar${compact ? " library-avatar-compact" : ""}`} src={profile.imageUrl} alt="" />
    : <span className={`library-avatar library-avatar-fallback${compact ? " library-avatar-compact" : ""}`} aria-hidden="true">{profile.displayName.charAt(0).toUpperCase()}</span>;
}

function LibraryPassportVisual() {
  return (
    <div className="library-hero-visual" aria-label="A shelf of reusable marketing skill passports">
      <div className="library-visual-shelf">
        <span className="library-book library-book-blue"><small>SEO</small><strong>Search brief</strong></span>
        <span className="library-book library-book-green"><small>VOC</small><strong>Research notes</strong></span>
        <span className="library-book library-book-cream"><small>LIFECYCLE</small><strong>Email review</strong></span>
      </div>
      <div className="library-visual-passport">
        <p>SKILL PASSPORT</p>
        <DoorwayBrandIcon name="door-outline-green" />
        <strong>Positioning brief</strong>
        <span>Content only · Claude + Codex</span>
        <i>✓ Checked by Doorway</i>
      </div>
    </div>
  );
}

function ProfileSetup({ onSaved }: { onSaved: (profile: PublicSkillProfile) => void }) {
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  return (
    <form className="library-profile-setup" onSubmit={(event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const body = new FormData(event.currentTarget);
      setSaving(true);
      setError("");
      void saveMyPublicSkillProfile({
        handle: String(body.get("handle") ?? ""),
        displayName: String(body.get("displayName") ?? ""),
        bio: String(body.get("bio") ?? ""),
        websiteUrl: String(body.get("websiteUrl") ?? ""),
      }).then(onSaved).catch((cause: unknown) => setError(message(cause, "That profile could not be saved."))).finally(() => setSaving(false));
    }}>
      <p className="eyebrow">First, claim your shelf</p>
      <h2>Choose your public author page.</h2>
      <div className="library-publish-grid">
        <label><span>Public name</span><input name="displayName" required maxLength={80} /></label>
        <label><span>Handle</span><div className="library-slug-field"><small>/library/</small><input name="handle" required pattern="[a-z0-9-]+" /></div></label>
        <label className="library-publish-wide"><span>Short bio</span><textarea name="bio" maxLength={500} rows={3} /></label>
        <label className="library-publish-wide"><span>Website</span><input name="websiteUrl" type="url" placeholder="https://" /></label>
      </div>
      {error && <p className="library-error" role="alert">{error}</p>}
      <button className="primary-button" type="submit" disabled={saving}>{saving ? "Saving…" : "Create author page"}</button>
    </form>
  );
}

function SkillFolderField({
  files,
  onFiles,
  teamSkillSource,
}: {
  files: File[];
  onFiles: (files: File[]) => void;
  teamSkillSource?: PublicTeamSkillSource;
}) {
  const hasSkillFile = useMemo(() => files.some((file) => (file.webkitRelativePath || file.name).endsWith("SKILL.md")), [files]);
  const selection = teamSkillSource
    ? {
        title: `Team Skill version ${teamSkillSource.versionNumber} is ready`,
        description: "Already stored in Doorway. It will be copied when you publish.",
      }
    : files.length
      ? {
          title: `${files.length} files selected`,
          description: hasSkillFile
            ? "SKILL.md found and ready to check."
            : "This folder needs a SKILL.md file.",
        }
      : undefined;
  return (
    <FolderPicker
      variant="skill"
      {...(selection ? { selection } : {})}
      onChoose={(chosen) => onFiles(Array.from(chosen))}
    />
  );
}

function LibraryLoading() {
  return <div className="library-loading" aria-live="polite"><span /><p>Opening the library…</p></div>;
}

function LibraryEmpty({ query }: { query: string }) {
  return <div className="library-empty"><DoorwayBrandIcon name="door-outline-green" /><h3>{query ? "No skill matches that search yet." : "This shelf is ready for its first skill."}</h3><p>Try a broader job or publish the workflow you were hoping to find.</p></div>;
}

function LibraryState({ title, copy }: { title: string; copy: string }) {
  return <section className="library-state"><DoorwayBrandIcon name="door-outline-green" /><h1>{title}</h1><p>{copy}</p><a className="secondary-button" href="/library">Return to the library</a></section>;
}

function capabilityLabel(value: string): string {
  return value === "content_only" ? "Content only" : "Uses tools or scripts";
}

function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 102.4) / 10} KB`;
  return `${Math.round(value / 1024 / 102.4) / 10} MB`;
}

function shortDigest(value: string): string {
  return value.length > 15 ? `${value.slice(0, 12)}…` : value;
}

function statusLabel(status: PublicSkillComparison["files"][number]["status"]): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function capabilityEntries(values: Record<string, string[]>): string[] {
  return Object.entries(values).flatMap(([field, entries]) => entries.map((entry) => `${field}: ${entry}`));
}

function provenanceTitle(sourceType: PublicSkillVersionDetail["provenance"]["sourceType"]): string {
  if (sourceType === "github") return "Imported from GitHub";
  if (sourceType === "team") return "Shared from a Team Skill";
  if (sourceType === "remix") return "Remixed in Doorway";
  if (sourceType === "seed") return "Curated by Doorway";
  if (sourceType === "legacy") return "Earlier Doorway version";
  return "Created in Doorway";
}

function provenanceDescription(version: PublicSkillVersionDetail): string {
  if (version.provenance.sourceType === "github") {
    return "Doorway resolved this package from the linked public repository and pinned this version to an exact commit.";
  }
  if (version.provenance.sourceType === "team") {
    return "This public package preserves the exact Team Skill version that its publisher selected.";
  }
  if (version.provenance.sourceType === "remix") {
    return "This package records the Doorway skill and version it was adapted from.";
  }
  if (version.provenance.sourceType === "seed") {
    return "Doorway created and validated this original skill for the curated marketing collection.";
  }
  if (version.provenance.sourceType === "legacy") {
    return "Doorway preserved this immutable version before structured source history was available.";
  }
  return "The publisher created and validated this package directly in Doorway.";
}

function parsedTags(value: string): string[] {
  try {
    const tags = JSON.parse(value);
    return Array.isArray(tags) ? tags.filter((tag): tag is string => typeof tag === "string") : [];
  } catch {
    return [];
  }
}

function truncateSuggestedText(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  const candidate = value.slice(0, maxLength + 1);
  const lastSpace = candidate.lastIndexOf(" ");
  return value.slice(0, lastSpace > maxLength * 0.75 ? lastSpace : maxLength).trimEnd();
}

function message(cause: unknown, fallback: string): string {
  return cause instanceof Error ? cause.message : fallback;
}

function useLibraryMetadata(title: string, description: string, path: string) {
  useEffect(() => {
    document.title = `${title} | Doorway`;
    setMeta("description", description);
    setMeta("robots", "index, follow, max-image-preview:large");
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.append(canonical);
    }
    canonical.href = new URL(path, "https://meetdoorway.com").toString();
  }, [description, path, title]);
}

function setMeta(name: string, content: string) {
  let tag = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.name = name;
    document.head.append(tag);
  }
  tag.content = content;
}
